import api from '../../../utils/api';

/**
 * Smart Categorization Service
 * Client-side service for smart transaction categorization features
 */
const smartCategorizationService = {
  /**
   * Get category suggestions for a transaction description
   * @param {string} description - Transaction description to categorize
   * @returns {Promise} - Promise with array of category suggestions
   */
  getCategorySuggestions: async (description) => {
    try {
      const response = await api.get('/api/smart-categorization/suggestions', {
        params: { description }
      });
      
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('Error getting category suggestions:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get category suggestions',
        error
      };
    }
  },
  
  /**
   * Get the best matching category for a description
   * @param {string} description - Transaction description to categorize
   * @returns {Promise} - Promise with best matching category
   */
  getBestMatch: async (description) => {
    try {
      const response = await api.get('/api/smart-categorization/best-match', {
        params: { description }
      });
      
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('Error getting best category match:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get best category match',
        error
      };
    }
  },
  
  /**
   * Categorize a specific transaction
   * @param {string} transactionId - ID of the transaction to categorize
   * @returns {Promise} - Promise with categorized transaction
   */
  categorizeTransaction: async (transactionId) => {
    try {
      const response = await api.post(`/api/smart-categorization/categorize/${transactionId}`);
      
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('Error categorizing transaction:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to categorize transaction',
        error
      };
    }
  },
  
  /**
   * Batch categorize multiple transactions
   * @param {Object} options - Categorization options
   * @returns {Promise} - Promise with categorization results
   */
  batchCategorize: async (options = {}) => {
    try {
      const response = await api.post('/api/smart-categorization/batch-categorize', options);
      
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('Error batch categorizing transactions:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to batch categorize transactions',
        error
      };
    }
  },
  
  /**
   * Parse merchant information from a transaction description
   * @param {string} description - Transaction description to parse
   * @returns {Promise} - Promise with parsed merchant info
   */
  parseMerchantInfo: async (description) => {
    try {
      const response = await api.post('/api/smart-categorization/parse-merchant', {
        description
      });
      
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('Error parsing merchant info:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to parse merchant info',
        error
      };
    }
  },
  
  /**
   * Update keywords for a category to improve matching
   * @param {string} categoryId - ID of the category to update
   * @param {Array} keywords - Array of keywords to set
   * @returns {Promise} - Promise with updated category
   */
  updateCategoryKeywords: async (categoryId, keywords) => {
    try {
      const response = await api.put(`/api/smart-categorization/keywords/${categoryId}`, {
        keywords
      });
      
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('Error updating category keywords:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update category keywords',
        error
      };
    }
  }
};

export default smartCategorizationService; 